;globalThis["_sentryRewritesTunnelPath"] = undefined;globalThis["SENTRY_RELEASE"] = undefined;globalThis["_sentryBasePath"] = undefined;globalThis["_sentryNextJsVersion"] = "15.5.27";globalThis["_sentryRewriteFramesAssetPrefixPath"] = "";globalThis["_sentryAssetPrefix"] = "";globalThis["_sentryExperimentalThirdPartyOriginStackFrames"] = undefined;globalThis["_sentryRouteManifest"] = "{\"isrRoutes\":[],\"dynamicRoutes\":[],\"staticRoutes\":[]}";import {datadogRum} from '@datadog/browser-rum';
import {
  captureRouterTransitionStart,
  type ErrorEvent,
  globalHandlersIntegration,
  init,
} from '@sentry/nextjs';

// As of next 15.3 the `next/config` package gets initialized after this script runs
// so we must manually parse __NEXT_DATA__ for the public runtime config
const nextConfig = JSON.parse(document.getElementById('__NEXT_DATA__')?.textContent || '{}');
const {
  datadog: datadogConfig,
  environment,
  graphqlHost,
  sentryClientDsn,
} = nextConfig.runtimeConfig || {};

/**
 * Sentry initialization
 */

const IGNORED_THIRD_PARTY_BREADCRUMB_URLS = [
  'https://253-ct.c3tag.com/',
  'https://api.hubspot.com/',
  'https://cdn.transcend.io/',
  'https://ct.pinterest.com/',
  'https://edge.fullstory.com/',
  'https://rs.fullstory.com/',
  'https://forms.hubspot.com/',
  'https://maps.googleapis.com/',
  'https://ampcid.google.com/',
  'https://browser-intake-datadoghq.com/',
  'https://s.yimg.com/',
  'https://tags.srv.stackadapt.com/',
  'https://ws.zoominfo.com/',
  'https://cm.teads.tv/',
  'https://js.zi-scripts.com/',
  'https://evnt.byspotify.com/',
  'https://intentstream.contanuity.com/',
  'https://app.leadsrx.com/',
  'https://imp.i319830.net/',
  'https://www.redditstatic.com/',
  'https://conversions-config.reddit.com/',
  'https://js.zi-scripts.com/',
  'https://t.teads.tv/',
  'https://u.clarity.ms/',
  'https://px.ads.linkedin.com/',
];

if (sentryClientDsn) {
  init({
    enabled: process.env.NODE_ENV === 'production' && window.location.protocol !== 'file:',
    dsn: sentryClientDsn,
    environment,
    release: process.env.CONFIG_BUILD_ID,
    integrations: [globalHandlersIntegration({onerror: false, onunhandledrejection: true})],
    allowUrls: [
      /\.ezcater\.com/i,
      /\.staging-ezcater.com/i,
      /\.sandbox-ezcater.com/i,
      /\.cdn-ezcater.com/i,
      // google maps
      /\.googleapis.com/i,
    ],
    ignoreErrors: [
      /InvalidStateError.*XMLHttpRequest.*state must be OPENED/i, // Third-party tracking (GTM)
      /PermissionDescriptor 'midi'/i, // Third-party MIDI permission probe (FUNC-458)
    ],
    beforeSend(event, hint) {
      // ignore reporting unhandled errors unless it came from GraphQL
      if (hint.mechanism?.handled === false && hint.mechanism.type === 'onunhandledrejection') {
        const error = hint.originalException as Error;
        if (
          error?.name !== 'ApolloError' &&
          !event.breadcrumbs?.slice(-10).find(crumb => crumb.category === 'GraphQL Error')
        ) {
          return null;
        }
      }

      const additionalTags: NonNullable<ErrorEvent['tags']> = {
        datadogSessionReplayLink: datadogRum.getSessionReplayLink(),
      };
      if (typeof window.FS?.getCurrentSessionURL === 'function') {
        additionalTags.fullstoryLink = window.FS.getCurrentSessionURL(true);
      }

      return {
        ...event,
        tags: {...event.tags, ...additionalTags},
      };
    },
    beforeBreadcrumb(breadcrumb) {
      // ignore reporting breadcrumbs for our third party scripts,
      // as long as they are HTTP OK (200 <= {http status code} < 300)
      if (
        (breadcrumb.category === 'fetch' || breadcrumb.category === 'xhr') &&
        breadcrumb.data?.status_code >= 200 &&
        breadcrumb.data?.status_code < 300 &&
        breadcrumb.data?.url &&
        IGNORED_THIRD_PARTY_BREADCRUMB_URLS.some(url => breadcrumb.data?.url?.startsWith(url))
      ) {
        return null;
      }

      return breadcrumb;
    },
  });
}

// This export will instrument router navigations
export const onRouterTransitionStart = captureRouterTransitionStart;

/**
 * Datadog RUM initialization
 */

// as of datadog sdk version 4 the version, environment, and service
// must all start with a letter (see: https://docs.datadoghq.com/getting_started/tagging/#defining-tags)
// our build IDs always start with a number, so we add "v" to the beginning for datadog to be happy
const version = `v${process.env.CONFIG_BUILD_ID}`;

if (datadogConfig?.rumClientToken && datadogConfig?.rumApplicationId) {
  datadogRum.init({
    allowedTracingUrls: [new URL(graphqlHost).origin],
    applicationId: datadogConfig.rumApplicationId,
    clientToken: datadogConfig.rumClientToken,
    defaultPrivacyLevel: 'mask-user-input',
    enableExperimentalFeatures: ['feature_flags'],
    env: environment,
    service: 'store-next',
    sessionReplaySampleRate: 100,
    sessionSampleRate: 100,
    site: 'datadoghq.com',
    trackLongTasks: true,
    trackResources: true,
    trackUserInteractions: true,
    version,
  });
}

(window as any)._fs_ready = () => {
  const fullstoryLink = window.FS.getCurrentSessionURL();
  datadogRum.setGlobalContextProperty('fullstoryLink', fullstoryLink);
};
